import supabase from '../../supabase';
import authHelper from '../../utils/authHelper';

/**
 * Logger optimizado para debugging
 */
const logger = {
  info: (message, data = null) => {
    console.log(`ℹ️ [AuthService] ${message}`, data ? data : '');
  },
  success: (message, data = null) => {
    console.log(`✅ [AuthService] ${message}`, data ? data : '');
  },
  warn: (message, data = null) => {
    console.warn(`⚠️ [AuthService] ${message}`, data ? data : '');
  },
  error: (message, error = null) => {
    console.error(`❌ [AuthService] ${message}`, error ? error : '');
  },
  debug: (message, data = null) => {
    console.log(`🔍 [AuthService] DEBUG: ${message}`, data ? data : '');
  }
};

/**
 * ✅ OPTIMIZADO: Verifica si un email ya está registrado
 */
const checkEmailExists = async (email) => {
  try {
    logger.debug('Verificando email existente', { email });
    
    // Usar Promise.allSettled para consultas paralelas sin fallar
    const [userResult, companyResult] = await Promise.allSettled([
      supabase
        .from('user_profiles')
        .select('id, email, membership_level')
        .eq('email', email)
        .limit(1),
      supabase
        .from('company_profiles')
        .select('id, email, subscription_tier')
        .eq('email', email)
        .limit(1)
    ]);
    
    const userExists = userResult.status === 'fulfilled' && 
                      userResult.value.data && 
                      userResult.value.data.length > 0;
                      
    const companyExists = companyResult.status === 'fulfilled' && 
                          companyResult.value.data && 
                          companyResult.value.data.length > 0;
    
    const exists = userExists || companyExists;
    const accountType = userExists ? 'individual' : companyExists ? 'company' : null;
    
    logger.debug('Resultado verificación email', {
      email,
      exists,
      userExists,
      companyExists,
      accountType
    });
    
    return {
      exists,
      accountType,
      userExists,
      companyExists
    };
    
  } catch (globalError) {
    logger.error('Error global en checkEmailExists', globalError);
    return { 
      exists: false, 
      error: globalError.message,
      fallback: true 
    };
  }
};

/**
 * ✅ OPTIMIZADO: Obtiene el siguiente número de miembro
 */
const getNextMemberNumber = async () => {
  try {
    logger.debug('Calculando siguiente número de miembro');
    
    // Consultas paralelas optimizadas
    const [userResult, companyResult] = await Promise.allSettled([
      supabase
        .from('user_profiles')
        .select('member_number')
        .not('member_number', 'is', null)
        .order('member_number', { ascending: false })
        .limit(5),
      supabase
        .from('company_profiles')
        .select('member_number')
        .not('member_number', 'is', null)
        .order('member_number', { ascending: false })
        .limit(5)
    ]);

    const userNumbers = userResult.status === 'fulfilled' 
      ? (userResult.value.data || []).map(p => p.member_number || 0)
      : [];
      
    const companyNumbers = companyResult.status === 'fulfilled'
      ? (companyResult.value.data || []).map(p => p.member_number || 0)
      : [];
    
    const allNumbers = [...userNumbers, ...companyNumbers, 100000];
    const maxNumber = Math.max(...allNumbers);
    const nextNumber = maxNumber + 1;
    
    logger.success('Número de miembro calculado', {
      maxNumber,
      nextNumber
    });

    return nextNumber < 100001 ? 100001 : nextNumber;
  } catch (error) {
    logger.warn('Error calculando member_number, usando aleatorio', error);
    const randomNumber = Math.floor(100000 + Math.random() * 900000);
    return randomNumber;
  }
};

/**
 * ✅ OPTIMIZADO: Crea perfil de usuario con manejo robusto de errores
 */
const createUserProfile = async (user, userType = 'individual') => {
  try {
    logger.info(`Creando perfil de ${userType}`, { 
      userId: user.id, 
      email: user.email
    });

    const isCompany = userType === 'company';
    const table = isCompany ? 'company_profiles' : 'user_profiles';
    
    // Verificar si ya existe con manejo de errores
    const { data: existing, error: checkError } = await supabase
      .from(table)
      .select('id, member_number')
      .eq('id', user.id)
      .maybeSingle();

    if (checkError && checkError.code !== 'PGRST116') {
      logger.warn(`Error verificando ${table}:`, checkError);
    }

    if (existing) {
      logger.success(`Perfil ya existe en ${table}`, existing);
      return existing;
    }

    // Obtener número de miembro
    const memberNumber = await getNextMemberNumber();
    
    // Preparar datos base
    const now = new Date().toISOString();
    const dateOnly = now.split('T')[0];

    const baseData = {
      id: user.id,
      email: user.email,
      member_number: memberNumber,
      created_at: now,
      updated_at: now,
      is_founding_member: false
    };

    // Datos específicos por tipo. `member_since` sólo existe como columna en
    // user_profiles — para company_profiles NO va.
    let insertData;
    if (isCompany) {
      insertData = {
        ...baseData,
        company_name: user.email.split('@')[0] || 'Empresa',
        subscription_tier: 'bronze'
      };
    } else {
      insertData = {
        ...baseData,
        member_since: dateOnly,
        membership_level: 'bronze', // 6 meses gratis
        is_admin: false,
        is_trainer: false
      };
    }

    logger.debug(`Insertando en ${table}`, insertData);

    // Insertar con manejo de conflictos
    const { data, error } = await supabase
      .from(table)
      .insert(insertData)
      .select()
      .single();

    if (error) {
      logger.error(`Error insertando en ${table}`, {
        error: error.message,
        code: error.code,
        details: error.details
      });
      
      // Si es un conflicto de ID único, intentar obtener el existente
      if (error.code === '23505') {
        logger.warn('Conflicto de ID único, obteniendo perfil existente...');
        const { data: existingProfile } = await supabase
          .from(table)
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (existingProfile) {
          logger.success('Perfil existente recuperado', existingProfile);
          return existingProfile;
        }
      }
      
      throw error;
    }

    logger.success(`Perfil creado exitosamente en ${table}`, data);
    return data;

  } catch (error) {
    logger.error('Error en createUserProfile', {
      message: error.message,
      userType,
      userId: user?.id
    });
    throw error;
  }
};

/**
 * ✅ OPTIMIZADO: Registro principal sin bucles
 */
const register = async (userData) => {
  try {
    logger.info('=== INICIANDO PROCESO DE REGISTRO ===');
    logger.debug('Datos recibidos', { email: userData.email, userType: userData.userType });

    const { email, password, userType, ...metadata } = userData;

    // Validaciones
    if (!email || !password) {
      const error = 'Email y contraseña son obligatorios';
      logger.error(error);
      return { success: false, error };
    }

    // userType ya NO se decide en /register — se elige post-verificación
    // (en VerifyEmailPage o en AuthCallbackPage para OAuth). Si llegara igual
    // por compatibilidad, lo guardamos en metadata como hint pero no se valida.

    // Verificar email existente
    logger.info('Verificando email existente...');
    const emailCheck = await checkEmailExists(email);

    if (emailCheck.exists && !emailCheck.fallback) {
      const message = `Email ya registrado como ${emailCheck.accountType}. Por favor, inicia sesión.`;
      logger.warn('Email ya existe', emailCheck);
      return {
        success: false,
        error: message,
        errorCode: 'EMAIL_ALREADY_EXISTS',
        existingAccountType: emailCheck.accountType
      };
    }

    // Crear usuario en Supabase Auth
    logger.info('Creando usuario en Supabase Auth...');

    const authMetadata = { ...metadata };
    if (userType) {
      authMetadata.user_type = userType;
      authMetadata.type = userType;
    }

    // emailRedirectTo: cuando el usuario hace click en el correo de confirmación,
    // tiene que aterrizar en /auth/verify (donde se procesa el token y se muestra
    // el selector Individual/Empresa). Sin esto, Supabase usa site_url que apunta
    // a /login y nuestro selector nunca corre.
    const emailRedirectTo = `${window.location.origin}/auth/verify`;

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: authMetadata,
        emailRedirectTo
      }
    });

    if (authError) {
      logger.error('Error en Supabase Auth signup', authError);
      
      if (authError.message.includes('already registered') || 
          authError.message.includes('already been registered')) {
        return {
          success: false,
          error: 'Este email ya está registrado. Por favor, inicia sesión.',
          errorCode: 'EMAIL_ALREADY_EXISTS'
        };
      }
      
      return {
        success: false,
        error: authError.message || 'Error al crear la cuenta'
      };
    }

    if (!authData || !authData.user) {
      logger.error('No se obtuvo usuario de Auth', authData);
      return {
        success: false,
        error: 'No se pudo crear el usuario'
      };
    }

    logger.success('Usuario creado en Auth', {
      id: authData.user.id,
      email: authData.user.email,
      emailConfirmed: authData.user.email_confirmed_at
    });

    // Cerrar sesión para forzar verificación de email
    logger.info('Cerrando sesión para forzar verificación...');
    await supabase.auth.signOut();

    const result = {
      success: true,
      user: authData.user,
      userType: userType || null,
      requiresVerification: !authData.user.email_confirmed_at,
      profileCreated: false
    };

    logger.success('=== REGISTRO COMPLETADO ===', result);
    return result;

  } catch (error) {
    logger.error('Error global en register', {
      message: error.message,
      userData: { email: userData.email, userType: userData.userType }
    });
    return {
      success: false,
      error: error.message || 'Error desconocido al registrar'
    };
  }
};

/**
 * ✅ OPTIMIZADO: Login con creación automática de perfil
 */
const login = async (email, password) => {
  try {
    logger.info('=== INICIANDO PROCESO DE LOGIN ===', { email });

    // Paso 1: Autenticar con Supabase
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      logger.error('Error en Auth login', error);
      throw error;
    }

    if (!data.user) {
      logger.error('No se obtuvo usuario de login');
      throw new Error('No se obtuvo usuario válido');
    }

    logger.success('Login exitoso en Auth', {
      id: data.user.id,
      email: data.user.email,
      emailConfirmed: data.user.email_confirmed_at
    });

    // NOTA: ya NO se auto-crea el perfil acá. Si el usuario no tiene perfil,
    // LoginPage detecta la ausencia y redirige a /auth/choose-type donde elige
    // si es Individual o Empresa y el perfil se crea con el tipo correcto.
    // Antes este auto-create asumía 'individual' por default y creaba perfiles
    // basura para empresas que nunca habían podido elegir.

    logger.success('=== LOGIN COMPLETADO ===');
    return data;

  } catch (error) {
    logger.error('❌ Error en login', error);
    throw error;
  }
};

/**
 * ✅ OPTIMIZADO: Logout limpio
 */
const logout = async () => {
  try {
    logger.info('Cerrando sesión...');
    
    // Cerrar sesión en Supabase
    const { error } = await supabase.auth.signOut();
    if (error) {
      logger.error('Error cerrando sesión en Supabase', error);
      throw error;
    }
    
    // Limpiar indicadores locales
    authHelper.clearAllSessionIndicators();
    
    logger.success('Sesión cerrada exitosamente');
  } catch (error) {
    logger.error('Error en logout', error);
    // Limpiar indicadores de todas formas
    authHelper.clearAllSessionIndicators();
    throw error;
  }
};

/**
 * ✅ OPTIMIZADO: Verificar estado de verificación de email
 */
const checkEmailVerificationStatus = async (email) => {
  try {
    logger.debug('Verificando estado de verificación', { email });
    
    // Obtener usuario actual
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      return {
        verified: false,
        needsVerification: true,
        error: 'Usuario no encontrado'
      };
    }
    
    const isVerified = !!user.email_confirmed_at;
    
    logger.debug('Estado de verificación', {
      email: user.email,
      verified: isVerified,
      confirmedAt: user.email_confirmed_at
    });
    
    return {
      verified: isVerified,
      needsVerification: !isVerified,
      user
    };
    
  } catch (error) {
    logger.error('Error verificando estado de email', error);
    return {
      verified: false,
      needsVerification: true,
      error: error.message
    };
  }
};

/**
 * ✅ OPTIMIZADO: Reenviar email de verificación
 */
const resendVerificationEmail = async (email) => {
  try {
    logger.info('Reenviando email de verificación', { email });
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email
    });
    
    if (error) {
      logger.error('Error reenviando email', error);
      throw error;
    }
    
    logger.success('Email de verificación reenviado');
    return { success: true };
    
  } catch (error) {
    logger.error('Error en resendVerificationEmail', error);
    return {
      success: false,
      error: error.message || 'Error al reenviar email'
    };
  }
};

/**
 * ✅ Solicitar email de recuperación de contraseña.
 * El correo lo genera Supabase Auth y se entrega vía Resend (SMTP configurado
 * en el dashboard de Supabase). Ver RESEND.md.
 */
const forgotPassword = async (email) => {
  try {
    logger.info('Solicitando recuperación de contraseña', { email });

    if (!email) {
      throw new Error('El email es obligatorio');
    }

    // El usuario aterriza en /reset-password con una sesión de recovery activa.
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });

    if (error) {
      logger.error('Error solicitando recuperación de contraseña', error);
      throw error;
    }

    logger.success('Email de recuperación enviado');
    return { success: true };

  } catch (error) {
    logger.error('Error en forgotPassword', error);
    throw error;
  }
};

/**
 * ✅ Establecer una nueva contraseña.
 * Requiere que el usuario haya llegado desde el link del email (sesión de
 * recovery activa que Supabase levanta automáticamente desde la URL).
 */
const resetPassword = async (newPassword) => {
  try {
    logger.info('Actualizando contraseña del usuario');

    if (!newPassword || newPassword.length < 6) {
      throw new Error('La contraseña debe tener al menos 6 caracteres');
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      logger.error('Error actualizando contraseña', error);
      throw error;
    }

    logger.success('Contraseña actualizada correctamente');
    return { success: true };

  } catch (error) {
    logger.error('Error en resetPassword', error);
    throw error;
  }
};

/**
 * ✅ Iniciar OAuth con Google.
 * El usuario es redirigido a Google y vuelve a /auth/callback con la sesión activa.
 * Si pasamos userType, lo propagamos en el redirectTo (query param) para que la
 * página de callback sepa qué tipo de perfil crear (individual o company).
 * Si no, la callback page muestra un selector.
 */
const signInWithGoogle = async (userType = null) => {
  try {
    logger.info('Iniciando OAuth con Google', { userType });

    const params = new URLSearchParams();
    if (userType) params.set('user_type', userType);
    const qs = params.toString();
    const redirectTo = `${window.location.origin}/auth/callback${qs ? `?${qs}` : ''}`;

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });

    if (error) {
      logger.error('Error iniciando OAuth con Google', error);
      throw error;
    }

    return { success: true, data };
  } catch (error) {
    logger.error('Error en signInWithGoogle', error);
    return { success: false, error: error.message || 'No se pudo iniciar Google Auth' };
  }
};

// ✅ EXPORTAR SERVICIOS OPTIMIZADOS
const authService = {
  register,
  login,
  logout,
  createUserProfile,
  getNextMemberNumber,
  checkEmailExists,
  checkEmailVerificationStatus,
  resendVerificationEmail,
  forgotPassword,
  resetPassword,
  signInWithGoogle
};

export default authService;